<template>
    <div class="notifications-content">
        <div class="kpi-container">
            <KPICard type="issues" iconClass="exclamation" :label="$t('home.issues.title')" :value="issueCounter" :linkText="$t('config.sensor.filter_view')" :active="activeFilter === 'issues'" @click="handleKpiClick" />
            <KPICard type="alerts" iconClass="menu-task" :label="$t('config.sensor.alerts')" :value="alertCounter" :linkText="$t('config.sensor.filter_view')" :active="activeFilter === 'alerts'" @click="handleKpiClick" />
        </div>

        <div class="notifications-list">
            <VirtualList
                v-if="notificationsRows && notificationsRows.length > 0"
                :keeps="8"
                :estimate-size="rowEstimateSize"
                class="virtual-list list-keep scroll-touch hide-scrollbar"
                :item-class="'list-item-keep'"
                :data-key="'id'"
                :data-sources="notificationsRows"
                :data-component="rowComponent"
                :extra-props="{ onSelect: handleNotificationSelect }"
            />

            <div v-else class="empty-state">
                <EmptyPage :title="$t('sensor.empty_alerts')" :description="$t('empty_pages.sensor_description')" />
            </div>
        </div>
    </div>
</template>

<script>
import NotificationCard from '@/components/domain/sensor/NotificationCard.vue'
import KPICard from '@/components/domain/ui/KPICard.vue'
import EmptyPage from '@/components/layout/EmptyPage.vue'
import VirtualList from 'vue-virtual-scroll-list'
import NotificationRow from '@/components/domain/sensor/NotificationRow.vue'

export default {
    name: 'NotificationsView',
    components: {
        NotificationCard,
        KPICard,
        EmptyPage,
        VirtualList,
        NotificationRow
    },
    props: {
        notifications: {
            type: Array,
            required: true
        },
        kpiData: {
            type: Object,
            required: true
        },
        activeFilter: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
            rowComponent: NotificationRow
        }
    },
    computed: {
        numColumns() {
            const w = this.viewportWidth
            if (w >= 1401) return 5
            if (w >= 1200) return 4
            if (w >= 950) return 3
            if (w >= 700) return 3
            if (w >= 500) return 2
            return 1
        },
        notificationsRows() {
            const items = this.notifications || []
            const cols = this.numColumns || 1
            const rows = []
            let row = { id: 1, columns: cols, items: [] }
            let idx = 0
            for (const it of items) {
                if (row.items.length === cols) {
                    rows.push(row)
                    row = { id: rows.length + 1, columns: cols, items: [] }
                }
                row.items.push({ sensorId: it.sensorId, timestamp: it.timestamp, count: it.count })
                idx++
            }
            if (row.items.length) rows.push(row)
            return rows
        },
        rowEstimateSize() {
            const w = this.viewportWidth
            if (w >= 1401) return 200
            if (w >= 1200) return 220
            if (w >= 950) return 240
            if (w >= 700) return 240
            if (w >= 500) return 270
            return 320
        },
        issueCounter() {
            return this.$store.getters['sensor/getIssueCounter']
        },
        alertCounter() {
            return this.$store.getters['sensor/getAlertCounter']
        }
    },
    methods: {
        handleKpiClick(type) {
            this.$emit('filter-change', type)
        },
        handleNotificationSelect(sensorId) {
            this.$emit('notification-select', sensorId)
        }
    }
    ,
    mounted() {
        if (typeof window !== 'undefined') {
            this._onResize = () => {
                this.viewportWidth = window.innerWidth
            }
            window.addEventListener('resize', this._onResize, { passive: true })
        }
    },
    beforeDestroy() {
        if (this._onResize) window.removeEventListener('resize', this._onResize)
    }
}
</script>

<style lang="scss" scoped>
.notifications-content {
    width: 100%;
    height: auto;
    flex: 1;
    min-height: 0;
    @include display-flex();
    @include flex-direction(column);
    overflow: hidden;

    .kpi-container {
        @include display-flex();
        gap: 12px;
        margin: 16px 0;
        flex-wrap: nowrap;
        flex-shrink: 0;

        ::v-deep .kpi-card {
            flex: 0 1 auto;
            max-width: 315px;
            min-width: 250px;
        }
    }

    .notifications-list {
        width: 100%;
        flex: 1;
        overflow: hidden;
        @include display-flex();
        @include flex-direction(column);

        .virtual-list {
            height: 100%;
            overflow: auto;
            margin-bottom: 16px;

        }

        .empty-state {
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            flex: 1;
        }
    }
}

// Mobile/Portrait responsive styles
@include media('<=1100px') {
    .notifications-content {
        .kpi-container {
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            -webkit-overflow-scrolling: touch;
            margin-bottom: 8px !important;

            // Hide scrollbar but keep functionality
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }

        .notifications-list {
            .notifications-grid {
                padding-bottom: 8px !important;
            }
        }
    }
}
</style>
